import React, { FC, useState } from 'react';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { ErrorNotificationComponent } from './ErrorNotificationComponent';
import { reInviteUser } from '../../store/merchnts/actions/application-details';
import { useNavigate, useParams } from 'react-router-dom';
import { resendInvitationToApplicant } from '../../store/identyti/actions/application';
import { useIdentity } from '../../store/identyti/identity-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { BUTTON_TYPE } from '../ButtonComponent';
import { APPLICATION_DETAILS_NAVIGATION } from '../../constants/application-details/application-details-navigation';
import { useLocation } from 'react-router';
import { fetchRegularApplicationData } from '../../store/merchnts/actions/application';

export const ApplicationDetailsNotification: FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { merchantState: { applicationData, isReinviteLoading }, dispatch } = useMerchants();
  const { identityState: { isResendInvitationLoading }, dispatch: identityDispatch } = useIdentity();
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const handleSuccess = (): void => {
    setModalState({
      isOpen: true,
      modalTitle: 'Success',
      minHeight: 150,
      content: (
        <>
          <FontAwesomeIcon icon={faCircleCheck} width={40} className="h-40px mb-10" />
          <div className="fs-5">Notification was successfully sent to</div>
          <span className="fw-bolder fs-5">{applicationData?.primaryContact?.email}.</span>
        </>
      ),
      confirmButtonText: 'Ok',
      onConfirm: async () => {
        closeModal();
        if (!applicationData?.primaryContact?.userCreated) {
          await fetchRegularApplicationData({ dispatch, applicationId });
        }
      },
      onClose: async () => {
        closeModal();
        if (!applicationData?.primaryContact?.userCreated) {
          await fetchRegularApplicationData({ dispatch, applicationId });
        }
      },
    });
  };

  const handleError = (): void => {
    setModalState({
      isOpen: true,
      modalTitle: 'Error',
      content: (
        <>
          <FontAwesomeIcon icon={faTriangleExclamation} width={40} className="h-40px mb-10 text-danger" />
          <div className="fs-5">Failed to send notification.</div>
        </>
      ),
      minHeight: 150,
      confirmButtonText: 'Ok',
      onConfirm: closeModal,
      onClose: closeModal,
    });
  };

  const closeModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

  const getAlertText = (): string => {
    switch (true) {
      case !applicationData?.primaryContact?.confirmedUser && applicationData?.primaryContact?.userCreated:
        return 'The applicant has not accepted the invite yet. Reinvite them so they can set their password and fill their application.';
        case !applicationData?.primaryContact?.userCreated:
        return 'The application has not been sent to the applicant yet. Please send it so they can create an account and complete the form.';
      default:
        return '';
    }
  };

  const getPrimaryButtonName = (): string => {
    switch (true) {
      case !applicationData?.primaryContact?.confirmedUser && applicationData?.primaryContact?.userCreated:
        return 'Reinvite';
        case !applicationData?.primaryContact?.userCreated:
        return 'Send application';
      default:
        return '';
    }
  };

  const getPrimaryAction = async (): Promise<void> => {
    switch (true) {
      case !applicationData?.primaryContact?.confirmedUser && applicationData?.primaryContact?.userCreated:
        await resendInvitationToApplicant({ dispatch: identityDispatch, email: applicationData?.primaryContact?.email, onSuccess: handleSuccess, onError: handleError });
        break;
        case !applicationData?.primaryContact?.userCreated:
        await reInviteUser({ dispatch, applicationId, onSuccess: handleSuccess, onError: handleError });
        break;
    }
  };

  const getPrimaryButtonAdditionalClasses = (): string => {
    if (applicationData?.primaryContact?.userCreated) {
      return 'w-120px';
    } else {
      return 'w-200px';
    }
  };

  const showErrorAlert = (!applicationData?.primaryContact?.confirmedUser && applicationData?.primaryContact?.userCreated) ||
    !applicationData?.primaryContact?.userCreated;

  return (
    <>
      {
        showErrorAlert && (
          <ErrorNotificationComponent
            title="Attention"
            text={getAlertText()}
            primaryButtonName={getPrimaryButtonName()}
            containerAdditionalClass="mb-5"
            primaryButtonAdditionalClass={getPrimaryButtonAdditionalClasses()}
            primaryButtonAction={getPrimaryAction}
            primaryButtonLoading={isReinviteLoading || isResendInvitationLoading}

            secondaryButtonName={!applicationData?.primaryContact?.userCreated && 'Review application'}
            secondaryButtonType={BUTTON_TYPE.FAKE_LINK}
            secondaryButtonWithoutUnderline
            secondaryButtonAction={(): void => navigate(`/accounts/applications/${applicationId}/${APPLICATION_DETAILS_NAVIGATION.FORM}`, {
              state: { ...state }
            })}
          />
        )
      }

      <ModalWindow
        {...modalState}
      />
    </>
  );
};